import { Injectable } from '@angular/core';
import Dexie from 'dexie';

const ORDER_LIST_CACHE_DATABASE = 'zalamo-orders';
const ORDER_LIST_CACHE_STORE_NAMES = ['orders'] as const;

@Injectable({
  providedIn: 'root',
})
export class OrderListCacheService {
  async clear(): Promise<void> {
    if (typeof indexedDB === 'undefined') {
      return;
    }

    const exists = await Dexie.exists(ORDER_LIST_CACHE_DATABASE);
    if (!exists) {
      return;
    }

    await this.clearExistingDatabaseStores();
  }

  private clearExistingDatabaseStores(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const openRequest = indexedDB.open(ORDER_LIST_CACHE_DATABASE);

      openRequest.onerror = () => {
        reject(this.createIndexedDbError('open', openRequest.error));
      };

      openRequest.onblocked = () => {
        reject(new Error('Order list cache database is blocked'));
      };

      openRequest.onsuccess = () => {
        const database = openRequest.result;
        const storeNames = ORDER_LIST_CACHE_STORE_NAMES.filter((storeName) =>
          database.objectStoreNames.contains(storeName),
        );

        if (!storeNames.length) {
          database.close();
          resolve();
          return;
        }

        const transaction = database.transaction(storeNames, 'readwrite');

        transaction.onerror = () => {
          database.close();
          reject(this.createIndexedDbError('clear', transaction.error));
        };

        transaction.onabort = () => {
          database.close();
          reject(this.createIndexedDbError('clear', transaction.error));
        };

        transaction.oncomplete = () => {
          database.close();
          resolve();
        };

        storeNames.forEach((storeName) => {
          transaction.objectStore(storeName).clear();
        });
      };
    });
  }

  private createIndexedDbError(
    action: string,
    error: DOMException | null,
  ): Error {
    return new Error(
      `Unable to ${action} order list cache: ${
        error?.message ?? 'unknown IndexedDB error'
      }`,
    );
  }
}
